import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Zap, Cake, RotateCcw, TrendingUp, Receipt, Clock } from "lucide-react";

const TRIGGERS = [
  { key: "birthday", title: "Birthday", icon: Cake, hint: "Sent to customers whose birthday matches today's date.", needsDays: false },
  { key: "win_back", title: "Win-back", icon: RotateCcw, hint: "Customers idle for at least N days. Single batch per branch.", needsDays: true, daysLabel: "Idle days threshold" },
  { key: "tier_upgrade", title: "Tier Upgrade", icon: TrendingUp, hint: "Auto-fired on bill confirmation when a customer crosses a tier threshold.", needsDays: false },
  { key: "post_purchase", title: "Post-purchase", icon: Receipt, hint: "Sent after every confirmed sale (queued for approval).", needsDays: false },
  { key: "points_expiry", title: "Points Expiry", icon: Clock, hint: "Reminder for customers with non-zero loyalty points.", needsDays: true, daysLabel: "Days advance notice" },
];

export default function Triggers() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => setCfg((await api.get("/whatsapp/triggers")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = {};
      for (const t of TRIGGERS) payload[t.key] = cfg[t.key];
      await api.put("/whatsapp/triggers", payload);
      toast.success("Triggers saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/whatsapp/triggers/run-now");
      toast.success(`Birthday: ${data.birthday?.created || 0} | Win-back: ${data.win_back?.created || 0} | Points-expiry: ${data.points_expiry?.created || 0} campaigns queued for approval.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!cfg) return <div className="text-slate-500">Loading…</div>;

  const updateField = (key, field, value) => setCfg({ ...cfg, [key]: { ...(cfg[key] || {}), [field]: value } });

  return (
    <div className="space-y-6" data-testid="triggers-page">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-600">Triggered campaigns auto-create entries in the Approval Queue. Super Admin reviews & sends them.</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={runNow} disabled={busy} className="rounded-none" data-testid="run-now-btn">
            <Zap className="w-4 h-4 mr-1" /> Run now
          </Button>
          <Button onClick={save} className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="triggers-save">Save</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {TRIGGERS.map((t) => {
          const v = cfg[t.key] || {};
          const Icon = t.icon;
          return (
            <div key={t.key} className="border border-slate-200 bg-white p-5 space-y-3" data-testid={`trigger-${t.key}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon className="w-5 h-5 text-slate-700" />
                  <div className="font-semibold">{t.title}</div>
                </div>
                <Switch checked={!!v.enabled} onCheckedChange={(c) => updateField(t.key, "enabled", c)} data-testid={`trigger-${t.key}-toggle`} />
              </div>
              <p className="text-xs text-slate-500">{t.hint}</p>
              {t.needsDays && (
                <div>
                  <Label className="text-xs">{t.daysLabel}</Label>
                  <Input type="number" min="1" value={v.days_threshold || 0} onChange={(e) => updateField(t.key, "days_threshold", parseInt(e.target.value) || 0)} className="rounded-none mt-1 max-w-[8rem]" data-testid={`trigger-${t.key}-days`} />
                </div>
              )}
              <div>
                <Label className="text-xs">Message Template</Label>
                <textarea rows={3} value={v.template || ""} onChange={(e) => updateField(t.key, "template", e.target.value)} className="w-full border border-slate-200 rounded-none p-2 mt-1 text-xs font-mono" data-testid={`trigger-${t.key}-template`} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
